import React from 'react';
import { motion } from 'framer-motion';
import Hero from '../components/ui/Hero';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiRotateCcw, FiHeart, FiUsers, FiTarget } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiHeart,
      title: 'Love',
      description: 'God\'s unconditional love is the foundation of everything we do. We love because He first loved us.'
    },
    {
      icon: FiRotateCcw,
      title: 'Transformation',
      description: 'We believe in the power of Jesus to completely transform lives - turning them 180 degrees toward hope.'
    },
    {
      icon: FiUsers,
      title: 'Authenticity',
      description: 'Real relationships built on honesty, vulnerability, and genuine care for one another.'
    },
    {
      icon: FiTarget,
      title: 'Bold Faith',
      description: 'We step out in courage to share the gospel and trust God for miraculous results.'
    }
  ];

  const timeline = [
    {
      year: '2018',
      title: 'Ministry Founded',
      description: 'Love 180 began with a vision to reach college students across Florida with the transforming love of Jesus.'
    },
    {
      year: '2019',
      title: 'First Campus Outreach',
      description: 'Launched regular outreach at University of Florida, seeing our first life transformations.'
    },
    {
      year: '2020',
      title: 'Expansion Begins',
      description: 'Despite challenges, expanded to 5 additional campuses and began city outreach initiatives.'
    },
    {
      year: '2022',
      title: 'Statewide Impact',
      description: 'Reached 15+ campuses across Florida with over 500 students encountered and lives changed.'
    },
    {
      year: '2024',
      title: 'New Horizons',
      description: 'Continuing to grow with new partnerships, volunteer teams, and innovative outreach methods.'
    }
  ];

  return (
    <div>
      <Hero
        title="Our Story"
        description="Love 180 exists to see lives completely transformed by the power of Jesus Christ. We believe that every person can experience a 180-degree turn from darkness to light, from hopelessness to hope."
        backgroundImage="https://images.unsplash.com/photo-1529390079861-591de354faf5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80"
      />

      {/* What 180 Means */}
      <Section>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
              What Does 180° Mean?
            </h2>
            <p className="text-lg text-neutral-600 mb-6">
              A 180-degree turn represents complete transformation - the opposite direction from where you started. When Jesus encounters a life, He doesn't just make small adjustments; He brings radical, life-changing transformation.
            </p>
            <p className="text-lg text-neutral-600 mb-6">
              We've witnessed students go from despair to hope, from addiction to freedom, from emptiness to purpose. This is the power of the gospel - it turns lives completely around.
            </p>
            <div className="bg-primary-50 p-6 rounded-lg">
              <p className="text-primary-800 font-medium italic">
                "Therefore, if anyone is in Christ, the new creation has come: The old has gone, the new is here!" - 2 Corinthians 5:17
              </p>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
              alt="Students in prayer"
              className="rounded-lg shadow-lg"
            />
          </div>
        </div>
      </Section>

      {/* Our Values */}
      <Section background="gray">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Our Core Values
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            These values guide everything we do as we reach out to students and young adults across Florida.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 text-center h-full">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-neutral-600">{value.description}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Founder Story Placeholder */}
      <Section>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
              alt="Ministry founder"
              className="rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
              A Heart for Students
            </h2>
            <p className="text-lg text-neutral-600 mb-6">
              Our founder's story begins with his own 180-degree transformation during college. After experiencing the life-changing power of Jesus firsthand, he felt called to share that same hope with students across Florida.
            </p>
            <p className="text-lg text-neutral-600 mb-6">
              What started as informal conversations with classmates grew into a passion for campus evangelism. Today, Love 180 represents the collective heart of our entire team - each member bringing their own story of transformation and calling to reach the next generation.
            </p>
            <div className="bg-neutral-50 p-6 rounded-lg">
              <p className="text-neutral-700 font-medium">
                "We don't just tell students about transformation - we live it every day. Our team represents what God can do when lives are completely surrendered to Him."
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Ministry Timeline */}
      <Section background="gray">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Our Journey
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            From a simple vision to a statewide movement, see how God has grown Love 180 over the years.
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-primary-200"></div>
          
          <div className="space-y-12">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className={`flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                  <Card className="p-6">
                    <div className="text-primary-600 font-bold text-lg mb-2">{item.year}</div>
                    <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                    <p className="text-neutral-600">{item.description}</p>
                  </Card>
                </div>
                
                <div className="hidden md:flex w-2/12 justify-center">
                  <div className="w-4 h-4 bg-primary-600 rounded-full border-4 border-white shadow-lg"></div>
                </div>
                
                <div className="hidden md:block w-5/12"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Theology of Transformation */}
      <Section>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
            Our Theology of Transformation
          </h2>
          <p className="text-lg text-neutral-600 mb-8">
            We believe transformation happens through authentic relationship with Jesus Christ. It's not about behavior modification or self-improvement - it's about encountering the living God who makes all things new.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 text-left">
            <div className="bg-neutral-50 p-6 rounded-lg">
              <h4 className="font-semibold text-neutral-900 mb-3">God's Initiative</h4>
              <p className="text-neutral-600">God is always the one who initiates transformation. We simply create opportunities for students to encounter His love.</p>
            </div>
            
            <div className="bg-neutral-50 p-6 rounded-lg">
              <h4 className="font-semibold text-neutral-900 mb-3">Gospel-Centered</h4>
              <p className="text-neutral-600">The good news of Jesus Christ is the power of God for salvation and transformation in every life.</p>
            </div>
            
            <div className="bg-neutral-50 p-6 rounded-lg">
              <h4 className="font-semibold text-neutral-900 mb-3">Community-Focused</h4>
              <p className="text-neutral-600">Transformation happens best in the context of authentic Christian community and discipleship relationships.</p>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default About;